import { useState, useCallback, useEffect, useRef, type FormEvent } from 'react';
import { Button, Card, Input } from '../ui';

export interface LoginScreenProps {
  /** Called when user submits email/password login */
  onLogin?: (email: string, password: string) => Promise<void>;
  /** Called when user clicks a social login button */
  onSocialLogin?: (provider: 'google' | 'apple') => Promise<void>;
  /** Called when user navigates to register */
  onNavigateRegister?: () => void;
  /** Whether the account is locked out */
  isLockedOut?: boolean;
  /** Remaining lockout time in seconds */
  lockoutRemainingSeconds?: number;
}

/**
 * Login screen with email/password form and social login buttons (Google, Apple).
 * Displays a generic "Invalid credentials" error on failure.
 * Shows lockout message with countdown timer when account is locked.
 *
 * @see Requirements 7.1, 7.2, 7.4, 7.6
 */
export function LoginScreen({
  onLogin,
  onSocialLogin,
  onNavigateRegister,
  isLockedOut = false,
  lockoutRemainingSeconds = 0,
}: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(lockoutRemainingSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorAnnouncementRef = useRef<HTMLDivElement>(null);

  // Update lockout timer when prop changes
  useEffect(() => {
    setLockoutTimer(lockoutRemainingSeconds);
  }, [lockoutRemainingSeconds]);

  // Countdown timer for lockout
  useEffect(() => {
    if (lockoutTimer > 0) {
      intervalRef.current = setInterval(() => {
        setLockoutTimer((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [lockoutTimer > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatLockoutTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!email.trim() || !password) {
        setError('Please enter your email and password');
        return;
      }

      setIsLoading(true);
      try {
        await onLogin?.(email.trim(), password);
      } catch {
        // Always display a generic error message (Requirement 7.6)
        setError('Invalid credentials');
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, onLogin]
  );

  const handleSocialLogin = useCallback(
    async (provider: 'google' | 'apple') => {
      setError(null);
      setIsLoading(true);
      try {
        await onSocialLogin?.(provider);
      } catch {
        setError('Invalid credentials');
      } finally {
        setIsLoading(false);
      }
    },
    [onSocialLogin]
  );

  const isFormDisabled = isLoading || isLockedOut || lockoutTimer > 0;

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
              <h1 className="text-heading-1 text-text-heading font-bold">Welcome Back</h1>
              <p className="mt-2 text-sm text-text-secondary">
                Sign in to your journal
              </p>
            </div>

            {/* Error announcement for screen readers */}
            <div ref={errorAnnouncementRef} aria-live="polite" aria-atomic="true">
              {error && (
                <div
                  className="rounded-sm bg-primary-light/30 border border-primary-dark/20 px-4 py-3 text-sm text-primary-dark"
                  role="alert"
                >
                  {error}
                </div>
              )}
            </div>

            {/* Lockout message */}
            {(isLockedOut || lockoutTimer > 0) && (
              <div
                className="rounded-sm bg-secondary-light/30 border border-secondary/30 px-4 py-3 text-sm text-text-primary"
                role="alert"
                aria-live="assertive"
              >
                <p className="font-medium">Account temporarily locked</p>
                <p className="mt-1 text-text-secondary">
                  Too many failed attempts. Please try again in{' '}
                  <span className="font-semibold" aria-label={`${lockoutTimer} seconds remaining`}>
                    {formatLockoutTime(lockoutTimer)}
                  </span>
                </p>
              </div>
            )}

            {/* Login form */}
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isFormDisabled}
                autoComplete="email"
                required
                aria-required="true"
              />

              <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isFormDisabled}
                autoComplete="current-password"
                required
                aria-required="true"
              />

              <Button
                type="submit"
                className="w-full"
                disabled={isFormDisabled}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-bg-card px-3 text-text-secondary">or continue with</span>
              </div>
            </div>

            {/* Social login buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant="secondary"
                disabled={isFormDisabled}
                onClick={() => handleSocialLogin('google')}
                aria-label="Sign in with Google"
              >
                <GoogleIcon />
                <span className="ml-2">Google</span>
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={isFormDisabled}
                onClick={() => handleSocialLogin('apple')}
                aria-label="Sign in with Apple"
              >
                <AppleIcon />
                <span className="ml-2">Apple</span>
              </Button>
            </div>

            {/* Register link */}
            <p className="text-center text-sm text-text-secondary">
              Don&apos;t have an account?{' '}
              <button
                type="button"
                className="text-primary-dark font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-sm"
                onClick={onNavigateRegister}
              >
                Create one
              </button>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
      />
    </svg>
  );
}
